import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { Input, useBaseInputStyle } from '@euler/components/form/Input';
import { FormElementCaret } from '@euler/components/form/elements-ui/Caret';
import { FormElementClear } from '@euler/components/form/elements-ui/Clear';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AnimatePresence } from 'moti';
import {
  Children,
  ReactElement,
  ReactNode,
  memo,
  useCallback,
  useRef,
} from 'react';
import {
  GestureResponderEvent,
  InteractionManager,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputFocusEventData,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { DropdownOption, DropdownRenderElementProps } from './types';

/** the default dropdown element implementation */
const DefaultDropdownElement = memo(
  <T,>({
    editable,
    componentStyles: styles,
    showCaretIcon = true,
    cloned = false,
    selectedOption,
    selectedOptions,
    formatValue,
    renderValue,
    renderDefaultItemContent,
    withTagsView,
    keyExtractor,
    onSearchQueryChange,
    onClear,
    onRemoveValue,
    ...props
  }: DropdownRenderElementProps<T> & {
    onRemoveValue?: (value: T) => void;
    editable: boolean;
    autoFocus?: boolean;
    withTagsView?: boolean;
    cloned?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const inputRef = useRef<TextInput>(null);

    const onClearPress = useCallback(
      (e: GestureResponderEvent) => {
        e.preventDefault();
        e.stopPropagation();

        onClear();

        if (
          props.searchable &&
          props.searchInputPlacement !== 'dropdown-menu'
        ) {
          void InteractionManager.runAfterInteractions(() => {
            inputRef.current?.focus();
          });
        }
      },
      [onClear, props.searchInputPlacement, props.searchable],
    );

    const onBlur = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        // a common issue on iOS Safari where the content doesn't
        // automatically reposition after keyboard dismissal
        Platform.OS === 'web' && window.scrollTo(0, 0);
        props.inputProps?.onBlur?.(e);
      },
      [props.inputProps],
    );

    const inputContainerStyle = useCallback(
      (focused: boolean) => {
        focused = (editable && focused) || (!editable && props.open) || cloned;
        return [
          styles.inputContainer(focused),
          focused ? styles.focusedInputContainer : undefined,
        ];
      },
      [cloned, editable, props.open, styles],
    );

    const showClearIcon =
      props.clearable &&
      ((props.searchable &&
        props.searchInputPlacement !== 'dropdown-menu' &&
        props.query) ||
        (!props.searchable &&
          ((props.mode === 'single' && props.value) ||
            (props.mode === 'multiple' && props.values?.length))));

    if (withTagsView) {
      const tagStyle = StyleSheet.flatten(styles.tag);
      const placeholderTextStyle: TextStyle = {
        marginTop: tagStyle?.marginTop ?? tagStyle.marginVertical,
        marginBottom: tagStyle?.marginBottom ?? tagStyle.marginVertical,
      };
      return (
        <TouchableOpacity
          style={[styles.container, props.style]}
          onPress={props.onOpen}
        >
          <TagsViewContainer
            placeholder={props.placeholder}
            placeholderTextColor={props.placeholderTextColor}
            placeholderTextStyle={placeholderTextStyle}
            style={[
              inputContainerStyle(false),
              {
                paddingRight:
                  (showCaretIcon ? rpx(40) : 0) + (showClearIcon ? rpx(32) : 0),
              },
            ]}
          >
            {selectedOptions?.map(option => (
              <TagItem
                option={option}
                key={keyExtractor(option.value)}
                removable={props.tagRemovable}
                formatTag={props.formatTag as any}
                renderTag={props.renderTag as any}
                style={styles.tag}
                textStyle={styles.tagText}
                removeIconStyle={styles.tagRemoveIcon?.style}
                removeIconColor={styles.tagRemoveIcon?.color}
                removeIconSize={styles.tagRemoveIcon?.size}
                onRemove={onRemoveValue as any}
              />
            ))}
          </TagsViewContainer>
          <AnimatePresence>
            {showClearIcon ? (
              <View style={styles.clearable?.style}>
                <FormElementClear
                  style={styles.clearable?.buttonStyle}
                  iconColor={styles.clearable?.color}
                  iconSize={styles.clearable?.size}
                  onPress={onClearPress}
                />
              </View>
            ) : null}
          </AnimatePresence>
          {showCaretIcon && (
            <TouchableOpacity
              style={[styles.caretIcon?.style]}
              onPress={props.onToggle}
            >
              <FormElementCaret
                style={styles.animatedIconStyle}
                iconSize={styles.caretIcon?.size}
              />
            </TouchableOpacity>
          )}
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        style={[styles.container, props.style]}
        disabled={props.disabled}
        onPress={props.onOpen}
      >
        <Input
          inputRef={inputRef}
          placeholder={props.placeholder}
          placeholderTextColor={props.placeholderTextColor}
          loading={props.loading}
          errorMsg={props.errorMsg}
          interacted={props.interacted}
          inputStyle={styles.input}
          editable={editable && !props.disabled}
          autoFocus={props.autoFocus}
          pointerEvents={editable ? 'auto' : 'none'}
          value={
            props.searchable
              ? props.query
              : formatValue?.(props.value) ?? selectedOption?.label ?? ''
          }
          onChangeText={onSearchQueryChange}
          {...props.inputProps}
          containerStyle={inputContainerStyle}
          onBlur={onBlur}
        />
        {renderValue != null && selectedOption != null ? (
          <View style={styles.valueWrap}>
            {renderValue(
              selectedOption,
              renderDefaultItemContent(selectedOption),
            )}
          </View>
        ) : null}
        {props.icon ? <View style={styles.icon}>{props.icon}</View> : null}
        {showClearIcon ? (
          <View style={styles.clearable?.style}>
            <FormElementClear
              style={styles.clearable?.buttonStyle}
              iconColor={styles.clearable?.color}
              iconSize={styles.clearable?.size}
              onPress={onClearPress}
            />
          </View>
        ) : null}
        {showCaretIcon && (
          <TouchableOpacity
            style={[styles.caretIcon?.style]}
            onPress={props.onToggle}
          >
            <FormElementCaret
              style={styles.animatedIconStyle}
              iconSize={styles.caretIcon?.size}
            />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  },
);

export default DefaultDropdownElement;

const TagsViewContainer = memo(
  ({
    style,
    placeholder,
    placeholderTextColor,
    placeholderTextStyle,
    children,
  }: {
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    placeholder?: string;
    placeholderTextColor?: string;
    placeholderTextStyle?: StyleProp<TextStyle>;
  }) => {
    const count = Children.count(children);
    const baseStyle = useBaseInputStyle();
    const containerStyle: StyleProp<ViewStyle> = [
      {
        backgroundColor: '#fff',
        borderRadius: baseStyle.borderRadius,
        borderWidth: baseStyle.borderWidth,
        padding: baseStyle.padding,
        borderColor: baseStyle.borderColor,
        flex: 1,
      },
    ];
    return (
      <StackLayout
        direction="row"
        flexWrap="wrap"
        spacing={0}
        style={[
          containerStyle,
          style,
          count === 0 ? { alignItems: 'center' } : undefined,
          { justifyContent: 'flex-start' },
        ]}
      >
        {count > 0 ? (
          children
        ) : (
          <Typography.Text
            size={baseStyle.fontSize}
            light
            color={placeholderTextColor ?? '#ccc'}
            style={placeholderTextStyle}
          >
            {placeholder ?? 'None'}
          </Typography.Text>
        )}
      </StackLayout>
    );
  },
);

const TagItem = memo(
  <T,>({
    option,
    removable,
    style,
    textStyle,
    removeIconStyle,
    removeIconColor,
    removeIconSize,
    formatTag,
    renderTag,
    onRemove,
  }: {
    option: DropdownOption<T>;
    removable?: boolean;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    removeIconStyle?: StyleProp<ViewStyle>;
    removeIconColor?: string;
    removeIconSize?: number;
    formatTag?: (value: T) => string;
    renderTag?: (value: T, defaultContent: ReactElement) => ReactElement;
    onRemove?: (value: T) => void;
  }) => {
    const value = option.value;
    const onRemovePress = useCallback(
      (e: GestureResponderEvent) => {
        e.preventDefault();
        e.stopPropagation();

        onRemove?.(value);
      },
      [onRemove, value],
    );

    const defaultContent = (
      <Typography.Text style={textStyle}>
        {formatTag ? formatTag(value) : option.label}
      </Typography.Text>
    );

    return (
      <StackLayout style={style} direction="row" alignItems="center">
        {renderTag?.(value, defaultContent) ?? defaultContent}
        {removable && (
          <Center.Touchable style={removeIconStyle} onPress={onRemovePress}>
            <Icon name="close" color={removeIconColor} size={removeIconSize} />
          </Center.Touchable>
        )}
      </StackLayout>
    );
  },
);
